import { FormEvent, useEffect, useState } from 'react'
import { Camera, ExternalLink, Globe, Lock } from 'lucide-react'
import { DENOMINATION_OPTIONS } from '@fellowship/shared'
import type { User } from '@fellowship/shared'
import { Avatar } from '../ui/Avatar'
import { ImageUpload } from '../admin/ImageUpload'
import { SettingsGroup } from './SettingsGroup'

export interface ProfileFormValues {
  displayName: string
  bio: string | null
  locationLabel: string
  denomination: string | null
  avatarUrl: string | null
  instagramUrl: string | null
  facebookUrl: string | null
  websiteUrl: string | null
}

interface ProfileFormProps {
  user: User
  onSave: (values: ProfileFormValues) => void
  saving: boolean
  saved?: boolean
}

const LABEL_STYLE = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 } as const

export function ProfileForm({ user, onSave, saving, saved }: ProfileFormProps) {
  const [displayName, setDisplayName] = useState(user.displayName ?? '')
  const [bio, setBio] = useState(user.bio ?? '')
  const [locationLabel, setLocationLabel] = useState(user.locationLabel ?? '')
  const [denomination, setDenomination] = useState(user.denomination ?? '')
  const [avatarUrl, setAvatarUrl] = useState<string | null>(user.avatarUrl ?? null)
  const [instagramUrl, setInstagramUrl] = useState(user.instagramUrl ?? '')
  const [facebookUrl, setFacebookUrl] = useState(user.facebookUrl ?? '')
  const [websiteUrl, setWebsiteUrl] = useState(user.websiteUrl ?? '')

  function reset() {
    setDisplayName(user.displayName ?? '')
    setBio(user.bio ?? '')
    setLocationLabel(user.locationLabel ?? '')
    setDenomination(user.denomination ?? '')
    setAvatarUrl(user.avatarUrl ?? null)
    setInstagramUrl(user.instagramUrl ?? '')
    setFacebookUrl(user.facebookUrl ?? '')
    setWebsiteUrl(user.websiteUrl ?? '')
  }

  useEffect(reset, [user])

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    onSave({
      displayName,
      bio: bio || null,
      locationLabel,
      denomination: denomination || null,
      avatarUrl: avatarUrl || null,
      instagramUrl: instagramUrl || null,
      facebookUrl: facebookUrl || null,
      websiteUrl: websiteUrl || null,
    })
  }

  return (
    <form onSubmit={handleSubmit}>
      <SettingsGroup label="Profile">
        <div style={{ padding: '16px 0' }}>
          {/* Avatar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
            <ImageUpload context="user-avatar" onUpload={(url) => setAvatarUrl(url)}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <Avatar name={displayName || '?'} src={avatarUrl} size={56} />
                <div style={{
                  position: 'absolute', bottom: 0, right: 0,
                  width: 22, height: 22, borderRadius: '50%',
                  background: 'var(--primary)', border: '2px solid var(--surface)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Camera size={11} strokeWidth={2.5} style={{ color: '#fff' }} />
                </div>
              </div>
            </ImageUpload>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>Profile photo</div>
              <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>PNG or JPG, up to 4MB</div>
              {avatarUrl && (
                <button
                  type="button"
                  onClick={() => setAvatarUrl(null)}
                  style={{ fontSize: 12, color: 'var(--warn)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', marginTop: 2 }}
                >
                  Remove photo
                </button>
              )}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label htmlFor="displayName" style={LABEL_STYLE}>Display name</label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="input"
              autoComplete="name"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label htmlFor="bio" style={LABEL_STYLE}>
              Bio <span style={{ fontWeight: 400, color: 'var(--muted)' }}>({bio.length}/300)</span>
            </label>
            <textarea
              id="bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={300}
              rows={3}
              placeholder="Tell people a bit about yourself…"
              className="input"
              style={{ resize: 'vertical', lineHeight: 1.5 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 14 }}>
            <div style={{ flex: 1 }}>
              <label htmlFor="locationLabel" style={LABEL_STYLE}>City or area</label>
              <input
                id="locationLabel"
                type="text"
                value={locationLabel}
                onChange={(e) => setLocationLabel(e.target.value)}
                placeholder="e.g. Riverside, CA"
                className="input"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label htmlFor="denomination" style={LABEL_STYLE}>Denomination</label>
              <select
                id="denomination"
                value={denomination}
                onChange={(e) => setDenomination(e.target.value)}
                className="input"
                style={{ cursor: 'pointer' }}
              >
                <option value="">Any / no preference</option>
                {DENOMINATION_OPTIONS.map((d) => (
                  <option key={d.key} value={d.key}>{d.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </SettingsGroup>

      {/* Pro Host social links */}
      <SettingsGroup
        label="Social links"
        action={
          !user.isProHost ? (
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', padding: '3px 8px', borderRadius: 99, background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--line-soft)' }}>
              PRO HOST
            </span>
          ) : undefined
        }
      >
        {user.isProHost ? (
          <div style={{ padding: '16px 0' }}>
            {[
              { id: 'instagramUrl', label: 'Instagram URL', value: instagramUrl, set: setInstagramUrl, ph: 'https://instagram.com/yourhandle', Icon: ExternalLink },
              { id: 'facebookUrl', label: 'Facebook URL', value: facebookUrl, set: setFacebookUrl, ph: 'https://facebook.com/yourpage', Icon: ExternalLink },
              { id: 'websiteUrl', label: 'Website URL', value: websiteUrl, set: setWebsiteUrl, ph: 'https://yoursite.com', Icon: Globe },
            ].map(({ id, label, value, set, ph, Icon }, i) => (
              <div key={id} style={{ marginBottom: i < 2 ? 12 : 0 }}>
                <label htmlFor={id} style={{ ...LABEL_STYLE, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Icon size={12} /> {label}
                </label>
                <input id={id} type="url" value={value} onChange={(e) => set(e.target.value)} placeholder={ph} className="input" />
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 0', fontSize: 13, color: 'var(--muted)' }}>
            <Lock size={13} /> Add Instagram, Facebook, and a website link with Pro Host.
          </div>
        )}
      </SettingsGroup>

      {/* Footer */}
      <div style={{ display: 'flex', gap: 10, paddingTop: 6, borderTop: '1px solid var(--line-soft)', marginBottom: 26 }}>
        <button type="submit" disabled={saving} className="btn" style={{ minWidth: 150 }}>
          {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={reset}
          style={{ height: 44, padding: '0 18px', borderRadius: 12, border: '1.5px solid var(--line)', background: 'transparent', fontSize: 13.5, fontWeight: 500, color: 'var(--ink-2)', cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
